import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from './api';
import { cn } from '../components/ui';

export interface Club {
  id: string;
  name: string;
  slug: string;
  hasLogo: boolean;
  logoVersion: string | null;
}

/**
 * Verein und Vereinslogo.
 *
 * Ohne Anmeldung ist der Name nicht abrufbar, das Logo dagegen schon - es
 * steht auf der Anmeldeseite. Deshalb zwei getrennte Wege: die Angaben über
 * die geschützte Abfrage, das Bild über eine feste Adresse.
 */
export function useClub(): Club | null {
  const { data } = useQuery({
    queryKey: ['club', 'current'],
    queryFn: () => api.get<Club | null>('/api/clubs/current'),
    staleTime: 5 * 60_000,
    retry: false,
  });
  return data ?? null;
}

/**
 * Die Fassung hängt am Zeitpunkt der letzten Änderung - sonst zeigt der
 * Browser nach einem Wechsel noch tagelang das alte Wappen.
 */
export function logoUrl(version?: string | null): string {
  return version ? `/api/clubs/logo?v=${encodeURIComponent(version)}` : '/api/clubs/logo';
}

/**
 * Das Wappen des Vereins, sonst die eingebaute Marke.
 *
 * Die Anmeldeseite weiß nicht, ob ein Logo hinterlegt ist - dort wird das Bild
 * einfach versucht und bei einem Fehler durch die Marke ersetzt.
 */
export function ClubLogo({
  club,
  size = 36,
  className,
}: {
  club?: Club | null;
  size?: number;
  className?: string;
}): JSX.Element {
  const [fehlgeschlagen, setFehlgeschlagen] = useState(false);
  const hatLogo = (club ? club.hasLogo : true) && !fehlgeschlagen;

  if (!hatLogo) {
    return (
      <span
        className={cn(
          'flex shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-brand-500 to-brand-700 font-bold text-white shadow-sm',
          className,
        )}
        style={{ width: size, height: size, fontSize: Math.round(size / 2.6) }}
      >
        UK
      </span>
    );
  }

  return (
    <img
      src={logoUrl(club?.logoVersion)}
      alt={club?.name ? `Wappen ${club.name}` : 'Vereinswappen'}
      width={size}
      height={size}
      className={cn('shrink-0 bg-transparent object-contain', className)}
      style={{ width: size, height: size }}
      onError={() => setFehlgeschlagen(true)}
    />
  );
}

/**
 * Setzt das Symbol im Browsertab auf das Vereinswappen.
 *
 * Ein Verein erkennt seinen Tab am eigenen Wappen; das Standardsymbol einer
 * fremden Anwendung sagt ihm nichts.
 */
export function useFavicon(club: Club | null): void {
  useEffect(() => {
    if (!club?.hasLogo) return;
    const vorhanden = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const link = vorhanden ?? document.createElement('link');
    const vorher = link.href;
    link.rel = 'icon';
    link.href = logoUrl(club.logoVersion);
    if (!vorhanden) document.head.appendChild(link);
    return () => {
      if (vorhanden) link.href = vorher;
      else link.remove();
    };
  }, [club?.hasLogo, club?.logoVersion]);
}
